import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { getCustomImageUrl } from '../../utils/imageProxy';
import {
  Plus,
  Search,
  Calendar,
  Eye,
  Edit3,
  Trash2,
  MoreVertical,
  Clock,
  FileText,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  BarChart3,
  Tag,
  Globe,
  ArrowUpRight,
  Sparkles
} from 'lucide-react';

interface Article {
  id: string;
  title: string;
  slug?: string;
  status: 'published' | 'scheduled' | 'draft';
  date: string;
  excerpt: string;
  banner_image: string;
  publish_date: string | null;
  views?: number;
  read_time?: number;
  seo_score?: number;
  tags?: string[];
  meta_title?: string;
  meta_description?: string;
}

export function AdminArticlesRevamped() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showActionMenu, setShowActionMenu] = useState<string | null>(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setArticles(
        data.map(article => ({
          id: article.id,
          title: article.title,
          slug: article.slug || '',
          status: article.published 
            ? (article.publish_date && new Date(article.publish_date) > new Date() 
              ? 'scheduled' 
              : 'published')
            : 'draft',
          date: article.created_at || '',
          excerpt: article.excerpt || '',
          banner_image: article.banner_image || '',
          publish_date: article.publish_date,
          views: Math.floor(Math.random() * 1000) + 100, // Demo data
          read_time: Math.floor(Math.random() * 10) + 3, // Demo data
          seo_score: Math.floor(Math.random() * 30) + 70, // Demo data
          tags: article.tags || [],
          meta_title: article.meta_title || undefined,
          meta_description: article.meta_description || undefined
        }))
      );
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const handleDelete = async (articleId: string) => {
    try {
      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', articleId);

      if (error) throw error;
      await fetchArticles();
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting article:', error);
    }
  };

  // Filter and sort articles
  const filteredArticles = articles
    .filter(article => {
      const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || article.status === filterStatus;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'updated':
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        case 'title':
          return a.title.localeCompare(b.title);
        case 'views':
          return (b.views || 0) - (a.views || 0);
        case 'seo':
          return (b.seo_score || 0) - (a.seo_score || 0);
        default:
          return 0;
      }
    });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'published':
        return <CheckCircle className="w-5 h-5" />;
      case 'scheduled':
        return <Clock className="w-5 h-5" />;
      case 'draft':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30';
      case 'scheduled':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30';
      case 'draft':
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/30';
      default:
        return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30';
    }
  };

  const getSeoScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 dark:from-blue-500/20 dark:via-purple-500/20 dark:to-pink-500/20 rounded-3xl p-8 backdrop-blur-sm border border-blue-200/50 dark:border-blue-700/50"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent mb-2">
              Article Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              Create and manage SEO-optimized content for your audience
            </p>
            
            {/* Quick Stats */}
            <div className="flex flex-wrap gap-6 mt-6">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-bold text-gray-900 dark:text-white">{articles.length}</span> Total Articles
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-bold text-gray-900 dark:text-white">
                    {articles.filter(a => a.status === 'published').length}
                  </span> Published
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-bold text-gray-900 dark:text-white">
                    {articles.reduce((sum, a) => sum + (a.views || 0), 0).toLocaleString()}
                  </span> Total Views
                </span>
              </div>
            </div>
          </div>
          
          <Link
            to="/admin/articles/new"
            className="group flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-medium hover:shadow-lg hover:scale-[1.02] transition-all duration-200 self-start"
          >
            <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" />
            Create New Article
          </Link>
        </div>
      </motion.div>

      {/* SEO Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-4"
      >
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Sparkles className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <span className="text-sm font-medium text-green-600 dark:text-green-400">Good</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {articles.filter(a => (a.seo_score || 0) >= 80).length}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">High SEO Score</p>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <TrendingUp className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Avg</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {Math.round(articles.reduce((sum, a) => sum + (a.seo_score || 0), 0) / articles.length) || 0}%
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Average SEO Score</p>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Tag className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {articles.filter(a => a.tags && a.tags.length > 0).length}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Tagged Articles</p>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-700/50 shadow-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {articles.filter(a => a.meta_title && a.meta_description).length}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">SEO Optimized</p>
        </div>
      </motion.div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl p-6"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search articles by title or content..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
          
          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          >
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="scheduled">Scheduled</option>
            <option value="draft">Draft</option>
          </select>
          
          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          >
            <option value="updated">Recently Updated</option>
            <option value="title">Title (A-Z)</option>
            <option value="views">Most Views</option>
            <option value="seo">SEO Score</option>
          </select>
        </div>
      </motion.div>

      {/* Articles List */}
      <div className="space-y-4">
        <AnimatePresence mode="popLayout">
          {filteredArticles.map((article) => (
            <motion.div
              key={article.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ delay: 0.05 }}
              className="group bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl hover:shadow-2xl transition-all duration-300 p-6"
            >
              <div className="flex items-start gap-6">
                {/* Article Image */}
                <div className="flex-shrink-0 w-48 h-32 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700">
                  {article.banner_image ? (
                    <img
                      src={getCustomImageUrl(article.banner_image, { width: 400, quality: 80, format: 'webp' })}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Article Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 line-clamp-1">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>

                    {/* Action Menu */}
                    <div className="relative ml-4">
                      <button
                        onClick={() => setShowActionMenu(showActionMenu === article.id ? null : article.id)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                      
                      <AnimatePresence>
                        {showActionMenu === article.id && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-10"
                          >
                            <a
                              href={`${window.location.origin}/blog/${article.slug || article.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                              onClick={() => window.open(`${window.location.origin}/blog/${article.slug || article.id}`, '_blank')}
                            >
                              <Eye className="w-4 h-4" />
                              View Article
                            </a>
                            <Link
                              to={`/admin/articles/edit/${article.id}`}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                              <Edit3 className="w-4 h-4" />
                              Edit Article
                            </Link>
                            <button
                              onClick={() => {
                                setShowDeleteConfirm(article.id);
                                setShowActionMenu(null);
                              }}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors w-full text-left"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Tags */}
                  {article.tags && article.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3 mb-3">
                      {article.tags.map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-md text-xs text-gray-600 dark:text-gray-400">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="flex flex-wrap items-center gap-6 text-sm">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${getStatusColor(article.status)}`}>
                      {getStatusIcon(article.status)}
                      <span className="font-medium">{article.status.charAt(0).toUpperCase() + article.status.slice(1)}</span>
                    </span>

                    <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Calendar className="w-4 h-4" />
                      {format(new Date(article.date), 'MMM dd, yyyy')}
                    </div>

                    <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Eye className="w-4 h-4" />
                      {article.views?.toLocaleString()} views
                    </div>

                    <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                      <Clock className="w-4 h-4" />
                      {article.read_time} min read
                    </div>

                    {article.seo_score && (
                      <div className={`flex items-center gap-1.5 font-medium ${getSeoScoreColor(article.seo_score)}`}>
                        <BarChart3 className="w-4 h-4" />
                        SEO: {article.seo_score}%
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="flex items-center gap-2">
                  <Link
                    to={`/admin/articles/edit/${article.id}`}
                    className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
                  >
                    <Edit3 className="w-5 h-5" />
                  </Link>
                  <a
                    href={`${window.location.origin}/blog/${article.slug || article.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-all"
                    title={`View Live Article: ${article.slug || article.id}`}
                    onClick={() => window.open(`${window.location.origin}/blog/${article.slug || article.id}`, '_blank')}
                  >
                    <ArrowUpRight className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Empty State */}
      {filteredArticles.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <FileText className="w-24 h-24 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
            {searchTerm || filterStatus !== 'all' ? 'No articles found' : 'No articles yet'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchTerm || filterStatus !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Start creating engaging content for your audience'
            }
          </p>
          {!searchTerm && filterStatus === 'all' && (
            <Link
              to="/admin/articles/new"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-medium hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
            >
              <Plus className="w-5 h-5" />
              Create First Article
            </Link>
          )}
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDeleteConfirm(null)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-md w-full z-50"
            >
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Delete Article?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete this article? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

